// frontend/src/components/p2p/InfoTooltip.tsx
import { Info } from 'lucide-react'
import { ReactNode } from 'react'

interface Props {
  children: ReactNode
  /** Force open (useful for testing). */
  forceOpen?: boolean
}

/**
 * Hover/focus tooltip with an info icon trigger. Pure CSS — no portal,
 * no Radix. Positioned above its parent; flips to below if there's no room.
 *
 * Usage:
 *   <label>Prima <InfoTooltip>Es tu margen sobre paridad…</InfoTooltip></label>
 */
export function InfoTooltip({ children, forceOpen }: Props) {
  return (
    <span className="relative inline-block group/info align-middle">
      <button
        type="button"
        aria-label="Más información"
        className="text-dark-500 hover:text-uni-400 focus:text-uni-400 focus:outline-none inline-flex items-center cursor-help ml-1"
        onClick={(e) => e.preventDefault()}
      >
        <Info className="w-3.5 h-3.5" />
      </button>

      <span
        className={`
          ${forceOpen ? '' : 'invisible opacity-0 group-hover/info:visible group-hover/info:opacity-100 group-focus-within/info:visible group-focus-within/info:opacity-100'}
          transition-opacity duration-150
          absolute bottom-full left-1/2 -translate-x-1/2 mb-2
          w-64 px-3 py-2.5 z-50
          rounded-lg border border-dark-700 bg-dark-900/95 backdrop-blur-md
          shadow-xl shadow-black/40
          text-xs leading-relaxed text-dark-200 font-normal
          pointer-events-none
        `}
        role="tooltip"
      >
        {children}
        {/* Caret */}
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-px w-0 h-0 border-4 border-transparent border-t-dark-700" />
        <span className="absolute top-full left-1/2 -translate-x-1/2 -mt-[5px] w-0 h-0 border-[3px] border-transparent border-t-dark-900" />
      </span>
    </span>
  )
}
